// Vendor Imports
import _ from 'lodash';
import type React from 'react';
import { useRef, useState } from 'react';

// Project Imports
import { I18n } from 'common/i18n';
import Slider from 'common/components/Slider';
import { geoSearch } from 'common/mapbox';
import { FilterFooter } from '../FilterFooter';
import { useFocusFirstActionableElement } from './InputFocus';
import { type FilterColumnsMap, type FilterEditorProps } from '../types';
import * as RadiusFilter from '../lib/Filters/RadiusFilter';
import { type NoopFilter, type RadiusSoqlFilter } from '../SoqlFilter';
import * as BaseFilter from '../lib/Filters/BaseFilter';
import { ForgeAutocomplete, ForgeIcon, ForgeTextField } from '@tylertech/forge-react';
import { type IAutocompleteOption } from '@tylertech/forge';

const DEFAULT_RADIUS = 2;
const SLIDER_MIN_RADIUS = 0.01;
const MAX_RADIUS = 10;
const RADIUS_STEP = 1;
const DEBOUNCE_WAIT_TIME = 400; // in milliseconds
const INPUT_FIELD_MIN_RADIUS = 0;

const sanitizeRadius = (radius: string | number): number => {
  const roundedNumber = _.round(Number(radius), 2);

  return _.clamp(roundedNumber, SLIDER_MIN_RADIUS, MAX_RADIUS);
};

const scope = 'shared.components.filter_bar.radius_filter';

export interface RadiusFilterEditorProps extends FilterEditorProps {
  appliedFilter: RadiusSoqlFilter | NoopFilter;
  columns: FilterColumnsMap;
}

function getInitialState(appliedFilter: RadiusFilterEditorProps['appliedFilter']) {
  const center = _.get(appliedFilter, 'arguments[0].center', []);
  const humanReadableLocation = _.get(appliedFilter, 'arguments[0].humanReadableLocation', '');
  const radius = _.get(appliedFilter, 'arguments[0].radius', DEFAULT_RADIUS);

  return { humanReadableLocation, center, radius: sanitizeRadius(radius) };
}

const optionBuilder = (option: IAutocompleteOption) => `<span>${option.label}</span>`;

export function RadiusFilterEditor(props: RadiusFilterEditorProps) {
  const { appliedFilter, columns, constraints, isReadOnly, onRemove, onUpdate, showRemoveButtonInFooter } =
    props;

  const [center, setCenter] = useState<number[]>(() => getInitialState(appliedFilter).center);
  const [radius, setRadius] = useState<number>(() => getInitialState(appliedFilter).radius);
  const [humanReadableLocation, setHumanReadableLocation] = useState<string>(
    () => getInitialState(appliedFilter).humanReadableLocation,
  );
  const [prevAppliedFilter, setPrevAppliedFilter] = useState(appliedFilter);

  const radiusFilterRef = useFocusFirstActionableElement<HTMLDivElement>();

  if (!_.isEqual(appliedFilter, prevAppliedFilter)) {
    setPrevAppliedFilter(appliedFilter);
    const initialState = getInitialState(appliedFilter);
    setCenter(initialState.center);
    setRadius(initialState.radius);
    setHumanReadableLocation(initialState.humanReadableLocation);
  }

  // Without a debounce, whenever the user changes the radius either using the text box or
  // the increment/decrement buttons in the text box or using the slider, it immediately updates
  // the filter in the vif, resulting in soql queries. If the user continuously clicks on the
  // increment button in the text box, without debounce, it will fire a lot of soql queries.
  //
  // The ref indirection keeps the debounce timer stable across re-renders while always reading
  // the latest center/radius/humanReadableLocation once the debounce actually fires.
  const onChangeNow = () => {
    const updatedFilter = RadiusFilter.getRadiusFilter(
      appliedFilter,
      columns,
      center,
      radius,
      humanReadableLocation,
    );
    onUpdate(updatedFilter, {
      shouldCloseControl: false,
    });
  };
  const onChangeNowRef = useRef(onChangeNow);
  onChangeNowRef.current = onChangeNow;

  const onChangeRef = useRef<ReturnType<typeof _.debounce>>();
  if (!onChangeRef.current) {
    onChangeRef.current = _.debounce(() => onChangeNowRef.current(), DEBOUNCE_WAIT_TIME);
  }
  const onChange = onChangeRef.current;

  const getSearchResults = async (searchTerm: string) => {
    const geoSearchOptions = _.get(constraints, 'geoSearch');

    const results = await geoSearch(searchTerm, geoSearchOptions);
    return results.map((result: any) => ({
      label: result.title,
      value: result,
    }));
  };

  const getFilterResults = (searchTerm: string) => {
    if (_.isEmpty(searchTerm)) {
      return;
    }
    return getSearchResults(searchTerm);
  };

  const onLocationSelection = (title: string, selection: { geometry: { coordinates: number[] } }) => {
    setHumanReadableLocation(_.get(selection, 'title'));
    setCenter(_.get(selection, 'geometry.coordinates'));
    onChange();
  };

  const onRadiusChange = (newRadius: string | number) => {
    setRadius(sanitizeRadius(newRadius));
    onChange();
  };

  const resetFilter = () => {
    onUpdate(BaseFilter.reset(appliedFilter, columns));
  };

  const placeholder = I18n.t('location_input_placeholder', { scope });
  const defaultValue =
    humanReadableLocation.length > 0 ? { label: humanReadableLocation, value: humanReadableLocation } : null;

  return (
    <div className="filter-controls radius-filter" ref={radiusFilterRef}>
      <div className="radius-filter-container">
        <ForgeAutocomplete
          className="geocode-autocomplete"
          placeholder
          mode="stateless"
          optionBuilder={optionBuilder}
          filter={getFilterResults}
          on-forge-autocomplete-select={(event: CustomEvent) => {
            onLocationSelection(event.detail.value.title, event.detail.value);
          }}
          value={defaultValue}
        >
          <ForgeTextField density="small">
            <ForgeIcon name="search" slot="start" />
            <label htmlFor="search-radius-filter-input">{placeholder}</label>
            <input id="search-radius-filter-input" type="text"></input>
          </ForgeTextField>
        </ForgeAutocomplete>
        <div className="radius-input-field-container">
          <ForgeTextField label-position="inline-start" className="radius-input-field">
            <label>{I18n.t('radius_field_label', { scope })}</label>
            <input
              role="textbox"
              type="number"
              max={MAX_RADIUS}
              min={INPUT_FIELD_MIN_RADIUS}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => onRadiusChange(event.target.value)}
              step={RADIUS_STEP}
              value={radius}
            />
          </ForgeTextField>
        </div>
        <Slider
          formatLabel={(label: number) => _.round(label, 2).toString()}
          onChange={onRadiusChange as any}
          rangeMax={MAX_RADIUS}
          rangeMin={SLIDER_MIN_RADIUS}
          step={RADIUS_STEP}
          value={radius}
        />
      </div>
      <FilterFooter
        isReadOnly={isReadOnly}
        showApplyButton={false}
        onClickRemove={onRemove}
        onClickReset={resetFilter}
        showRemoveButton={showRemoveButtonInFooter}
      />
    </div>
  );
}
